// src/components/NewInvoice/LooseDiamondItemForm.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { Controller } from "react-hook-form";
import { motion } from "framer-motion";
import Select from "react-select";
import { FaTrash } from "react-icons/fa";
import { useSelector } from "react-redux";
import { handleDropDowns } from "../../config/dropDownHelpers";

const toast = (msg, type = "error") => {
  if (window?.toastr?.[type]) return window.toastr[type](msg);
  if (window?.toast?.[type]) return window.toast[type](msg);
  if (window?.notify) return window.notify(msg, { type });
  alert(msg);
};

const baseSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    borderColor: state.isFocused ? "#A78BFA" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(167,139,250,0.35)" : "none",
    fontSize: "12px",
    backgroundColor: "white",
  }),
  indicatorsContainer: (base) => ({ ...base, height: "30px" }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  clearIndicator: (base) => ({ ...base, padding: "4px" }),
  valueContainer: (base) => ({ ...base, padding: "0 6px", height: "30px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  menu: (base) => ({ ...base, fontSize: "12px", zIndex: 40 }),
  menuList: (base) => ({ ...base, maxHeight: "100px", overflowY: "auto" }),
  singleValue: (base) => ({ ...base, fontSize: "12px" }),
};

const withErr = (styles, err) =>
  !err
    ? styles
    : {
        ...styles,
        control: (base, state) => {
          const s = styles.control ? styles.control(base, state) : base;
          return {
            ...s,
            borderColor: "#ef4444",
            boxShadow: state.isFocused
              ? "0 0 0 2px rgba(239,68,68,.2)"
              : "none",
            "&:hover": { borderColor: "#ef4444" },
          };
        },
      };

const inputCls = (err) =>
  `w-full px-2 py-[6px] text-sm border rounded ${
    err
      ? "border-red-400 focus:outline-none focus:ring-2 focus:ring-red-300"
      : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-300"
  }`;

const LooseDiamondItemForm = ({
  control,
  register,
  errors,
  rowindex,
  fieldId,
  onDelete,
  watch,
  setValue,
  getValues,
  showDelete = true,
  cardHeightClass = "h-[40vh] sm:h-[40vh]",
  registerFirstField,
  selectedBranch,
}) => {
  const formRef = useRef(null);

  const path = (f) => `looseDiamonds.${rowindex}.${f}`;
  const getNum = (f) => Number(getValues(path(f)) || 0);

  const diamonds = useSelector((s) => s.diamonds?.diamonds) || [];
  const diamondSieves =
    useSelector((s) => s?.diamondSieves?.diamondSieves) || [];
  const diamondSizes = useSelector((s) => s?.diamondSizes?.diamondSizes) || [];
  const countersFromState = useSelector((s) => s.counters?.counters) || [];

  const diamondOptions =
    diamonds?.map((d) => ({
      value: String(d?._id),
      label: [d?.diamondName ?? ""].filter(Boolean).join(" "),
      info: d,
    })) || [];

  const uniqueOptions = (getterId, getterLabel) => {
    const seen = new Set();
    const out = [];
    for (const d of diamonds) {
      const id = getterId(d);
      const label = getterLabel(d);
      if (!id || !label) continue;
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ value: key, label });
    }
    return out;
  };

  const shapeOptions = useMemo(
    () =>
      uniqueOptions(
        (d) => d?.shape?._id || d?.shape,
        (d) => d?.shapeName
      ),
    [diamonds]
  );
  const colorOptions = useMemo(
    () =>
      uniqueOptions(
        (d) => d?.color?._id || d?.color,
        (d) => d?.colorName
      ),
    [diamonds]
  );
  const clarityOptions = useMemo(
    () =>
      uniqueOptions(
        (d) => d?.clarity?._id || d?.clarity,
        (d) => d?.clarityName
      ),
    [diamonds]
  );

  const selectedShape = watch(path("shape"));
  const selectedShapeId =
    selectedShape?.value || selectedShape?._id || selectedShape || "";

  const sieveOptions = useMemo(() => {
    const list = Array.isArray(diamondSieves) ? diamondSieves : [];
    const filtered = selectedShapeId
      ? list.filter(
          (it) =>
            String(it?.shape?._id || it?.shape) === String(selectedShapeId)
        )
      : list;
    return filtered.map((it) => ({
      value: String(it?._id),
      label: it?.sieve || it?.sieveName || "",
    }));
  }, [diamondSieves, selectedShapeId]);

  const sizeOptions = useMemo(() => {
    const list = Array.isArray(diamondSizes) ? diamondSizes : [];
    const anyHasShape = list.some(
      (it) => it?.shape?._id || it?.shapeId || it?.shape
    );
    const getShapeId = (it) =>
      String(it?.shape?._id || it?.shapeId || it?.shape || "");
    const filtered =
      selectedShapeId && anyHasShape
        ? list.filter((it) => getShapeId(it) === String(selectedShapeId))
        : list;
    return filtered.map((it) => ({
      value: String(it?._id),
      label: it?.size || it?.sizeName || it?.name || "",
    }));
  }, [diamondSizes, selectedShapeId]);

  useEffect(() => {
    const curSieve = getValues(path("sieve"));
    if (
      curSieve?.value &&
      !sieveOptions.some((o) => o.value === curSieve.value)
    )
      setValue(path("sieve"), null);
    const curSize = getValues(path("size"));
    if (curSize?.value && !sizeOptions.some((o) => o.value === curSize.value))
      setValue(path("size"), null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShapeId, sieveOptions, sizeOptions]);

  const recalc = () => {
    const wt = getNum("diaWt");
    const rWt = getNum("ratePerCt");
    const pcs = getNum("pcs");
    const rPc = getNum("ratePerPc");
    const gstRate = Math.max(0, getNum("gstRate"));
    const mrp = getNum("mrp");

    let amt = 0;
    if (wt > 0 && rWt > 0 && pcs === 0) amt = +(wt * rWt).toFixed(2);
    else if (pcs > 0 && rPc > 0) amt = +(pcs * rPc).toFixed(2);

    setValue(path("itemAmount"), +amt.toFixed(2));

    if (mrp > 0) {
      setValue(path("itemGstAmount"), 0);
      setValue(path("totalItemAmount"), +mrp.toFixed(2));
    } else {
      const gstAmt = +((amt * gstRate) / 100).toFixed(2);
      setValue(path("itemGstAmount"), gstAmt);
      setValue(path("totalItemAmount"), +(amt + gstAmt).toFixed(2));
    }
  };

  const applyManualFinal = (target) => {
    const mrp = getNum("mrp");
    if (mrp > 0) {
      toast("MRP item total is locked (tax included).", "error");
      setValue(path("totalItemAmount"), +mrp.toFixed(2));
      return;
    }
    const autoAmt = getNum("itemAmount");
    const autoGst = getNum("itemGstAmount");
    const autoTotal = +(autoAmt + autoGst).toFixed(2);

    if (!Number.isFinite(target) || target <= 0) {
      setValue(path("totalItemAmount"), autoTotal);
      return;
    }
    if (target < autoTotal - 0.001) {
      toast(
        "Final amount cannot be reduced here. Use invoice discount.",
        "error"
      );
      setValue(path("totalItemAmount"), autoTotal);
      return;
    }
    if (target <= autoTotal + 0.001) {
      setValue(path("totalItemAmount"), autoTotal);
      return;
    }

    const rate = Math.max(0, getNum("gstRate"));
    const deltaIncl = +(target - autoTotal).toFixed(2);
    const addPretax = +(deltaIncl / (1 + rate / 100)).toFixed(2);
    const newPretax = +(autoAmt + addPretax).toFixed(2);
    const newGst = +((newPretax * rate) / 100).toFixed(2);

    setValue(path("itemAmount"), newPretax);
    setValue(path("itemGstAmount"), newGst);
    setValue(path("totalItemAmount"), +(newPretax + newGst).toFixed(2));
  };

  const onDiamondPicked = (opt) => {
    const sel = opt?.info;
    if (!sel) return;

    setValue(path("unit"), sel?.unit || "carat");
    const setSel = (k, id, label) => {
      if (!id) return;
      setValue(path(k), { value: String(id), label: label || "" });
    };
    setSel("shape", sel?.shape?._id || sel?.shape, sel?.shapeName);
    setSel("color", sel?.color?._id || sel?.color, sel?.colorName);
    setSel("clarity", sel?.clarity?._id || sel?.clarity, sel?.clarityName);
    setSel("sieve", sel?.sieve?._id || sel?.sieve, sel?.sieveName);
    setSel("size", sel?.size?._id || sel?.size, sel?.sizeName);
    if (typeof sel?.gstRate === "number")
      setValue(path("gstRate"), sel.gstRate);
    recalc();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!showDelete) return;
      if (e.ctrlKey && e.key === "Delete") {
        if (
          formRef.current &&
          formRef.current.contains(document.activeElement)
        ) {
          e.preventDefault();
          onDelete?.();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDelete, showDelete]);

  const hasNameErr = !!errors?.looseDiamonds?.[rowindex]?.diaName;
  const hasCounterErr = !!errors?.looseDiamonds?.[rowindex]?.counterRow;

  return (
    <motion.div
      ref={formRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: rowindex * 0.05 }}
      id={`loose-diamond-card-${fieldId}`}
      data-field-id={fieldId}
      className={`invoice-card ${cardHeightClass} snap-start px-2 pt-1 rounded-lg border border-gray-200 hover:border-gray-300 shadow-lg bg-white relative overflow-y-auto`}
    >
      <div className="mb-0.5 flex items-center justify-between sticky top-0 bg-white pt-1 pb-1">
        <h3 className="text-sm font-semibold text-gray-900">
          #{rowindex + 1} • Loose Diamond
        </h3>
        <div className="flex-1 h-px bg-gray-200 mx-2" />
        {showDelete && (
          <button
            type="button"
            onClick={onDelete}
            title="Delete this item"
            className="text-gray-500 hover:text-red-600 hover:scale-110 transition-all"
          >
            <FaTrash className="text-[14px]" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-12 gap-x-2 gap-y-1">
        {/* NEW: per-row Counter */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Counter <span className="text-red-500">*</span>
          </label>
          <Controller
            name={path("counterRow")}
            control={control}
            rules={{ required: "Counter is required" }}
            render={({ field }) => (
              <Select
                {...field}
                options={handleDropDowns({
                  key: "counter",
                  counters: countersFromState,
                  branch: selectedBranch,
                })}
                classNamePrefix="react-select"
                styles={withErr(baseSelectStyles, hasCounterErr)}
                placeholder="Select Counter"
                isClearable
                isSearchable
                onChange={(opt) => {
                  field.onChange(opt ?? null);
                  setValue(path("counterId"), opt?.value || "");
                  setValue(path("counterName"), opt?.label || "");
                  setValue(path("branchId"), opt?.info?.branchId || "");
                  setValue(path("branchName"), opt?.info?.branchName || "");
                }}
                ref={(selectRef) => {
                  if (registerFirstField && selectRef) {
                    registerFirstField(fieldId, {
                      focus: () => selectRef?.focus?.(),
                    });
                  }
                }}
              />
            )}
          />
        </div>

        {/* Name */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Name
          </label>
          <Controller
            name={path("diaName")}
            control={control}
            rules={{ required: "Diamond name is required" }}
            render={({ field }) => (
              <Select
                {...field}
                options={diamondOptions}
                classNamePrefix="react-select"
                styles={withErr(baseSelectStyles, hasNameErr)}
                placeholder="-- Select --"
                isClearable
                isSearchable
                menuPlacement="bottom"
                onChange={(opt) => {
                  field.onChange(opt);
                  onDiamondPicked(opt);
                }}
              />
            )}
          />
        </div>

        {/* Shape (locked) */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Shape
          </label>
          <Controller
            name={path("shape")}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={shapeOptions}
                isDisabled={true}
                classNamePrefix="react-select"
                styles={baseSelectStyles}
                isClearable
                isSearchable
              />
            )}
          />
        </div>

        {/* Color (locked) */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Color
          </label>
          <Controller
            name={path("color")}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={colorOptions}
                isDisabled={true}
                classNamePrefix="react-select"
                styles={baseSelectStyles}
                isClearable
                isSearchable
              />
            )}
          />
        </div>

        {/* Clarity (locked) */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Clarity
          </label>
          <Controller
            name={path("clarity")}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={clarityOptions}
                isDisabled={true}
                classNamePrefix="react-select"
                styles={baseSelectStyles}
                isClearable
                isSearchable
              />
            )}
          />
        </div>

        {/* Sieve */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Sieve
          </label>
          <Controller
            name={path("sieve")}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={sieveOptions}
                classNamePrefix="react-select"
                styles={baseSelectStyles}
                isClearable
                isSearchable
              />
            )}
          />
        </div>

        {/* Size */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Size
          </label>
          <Controller
            name={path("size")}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={sizeOptions}
                classNamePrefix="react-select"
                styles={baseSelectStyles}
                isClearable
                isSearchable
              />
            )}
          />
        </div>

        {/* Weight (ct) */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            Weight (ct)
          </label>
          <input
            type="text"
            {...register(path("diaWt"), { onChange: recalc })}
            className={inputCls()}
            placeholder="ct"
          />
        </div>

        {/* Rate / ct */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            Rate / ct
          </label>
          <input
            type="text"
            {...register(path("ratePerCt"), { onChange: recalc })}
            className={inputCls()}
            placeholder="₹/ct"
          />
        </div>

        {/* Pcs */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            Pieces
          </label>
          <input
            type="text"
            {...register(path("pcs"), { onChange: recalc })}
            className={inputCls()}
            placeholder="pcs"
          />
        </div>

        {/* Rate / Pcs */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            Rate / Pcs
          </label>
          <input
            type="text"
            {...register(path("ratePerPc"), { onChange: recalc })}
            className={inputCls()}
            placeholder="₹/pc"
          />
        </div>

        {/* Amount (pre-tax) */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Item Amount
          </label>
          <input
            type="text"
            {...register(path("itemAmount"))}
            disabled
            className="w-full px-2 py-[6px] text-sm border rounded bg-gray-50"
            placeholder="0.00"
          />
        </div>

        {/* GST % */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            GST %
          </label>
          <input
            type="text"
            {...register(path("gstRate"), { onChange: recalc })}
            className={inputCls()}
            placeholder="e.g. 3"
          />
        </div>

        {/* Tax */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">Tax</label>
          <input
            type="text"
            {...register(path("itemGstAmount"))}
            disabled
            className="w-full px-2 py-[6px] text-sm border rounded bg-gray-50"
            placeholder="0.00"
          />
        </div>

        {/* MRP (incl tax) */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            MRP (incl. tax)
          </label>
          <input
            type="text"
            {...register(path("mrp"), { onChange: recalc })}
            className={inputCls()}
            placeholder="0.00"
          />
        </div>

        {/* Final Amount */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Final Amount
          </label>
          <input
            type="text"
            {...register(path("totalItemAmount"), {
              onBlur: (e) => applyManualFinal(parseFloat(e.target.value)),
            })}
            className={inputCls()}
            placeholder="0.00"
          />
        </div>

        {/* Description */}
        <div className="md:col-span-3">
          <label className="block text-xs font-medium text-gray-700">
            Description
          </label>
          <input
            type="text"
            {...register(path("description"))}
            className={inputCls()}
            placeholder="Optional"
          />
        </div>
      </div>
    </motion.div>
  );
};

export default LooseDiamondItemForm;
